import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  title: string;
  /** The thing being deleted, e.g. "Hotel Hasselbacken". Renders bold. */
  entityName: string;
  /**
   * Body content above the checkbox - typically lists the side effects
   * of the action (rooms removed, screens unpaired, etc.). Plain string
   * or full ReactNode for multi-paragraph.
   */
  body: ReactNode;
  acknowledgeLabel?: string;
  confirmLabel?: string;
  /** When set, replaces the confirm button with an error Alert. */
  errorMessage?: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Two-step confirmation for irreversible actions: a required checkbox
 * gates the Confirm button. Both must be deliberate clicks - no
 * keyboard shortcuts that would let a fumbled enter delete the wrong
 * row.
 */
export default function DangerConfirmDialog({
  open,
  title,
  entityName,
  body,
  acknowledgeLabel,
  confirmLabel,
  errorMessage,
  busy,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the checkbox every time the dialog re-opens so a prior
  // session's "yes" doesn't carry over.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ color: "error.main" }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <Trans
            t={t}
            i18nKey="dangerConfirmDialog.aboutToDelete"
            values={{ name: entityName }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        <Box sx={{ mb: 2 }}>
          {typeof body === "string" ? (
            <Typography variant="body2" color="text.secondary">
              {body}
            </Typography>
          ) : (
            body
          )}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              size="small"
              data-testid="danger-confirm-checkbox"
            />
          }
          label={acknowledgeLabel ?? t("dangerConfirmDialog.acknowledge")}
        />
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!acknowledged || busy}
          onClick={onConfirm}
          data-testid="danger-confirm-button"
        >
          {busy ? t("states.working") : (confirmLabel ?? t("buttons.delete"))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
